/**
 * Sortable score results table for the dashboard with infinite scroll and score display.
 *
 * @module components/dashboard/scoreTable
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import React, {useEffect, useState} from 'react';
import moment from 'moment';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Pagination from '@material-ui/lab/Pagination';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Paper,
    TextField,
    Tooltip,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import {SaveAssessorNotes} from '../../services/result.service';
import DashboardPermissions from '../../permissions/dashboard';
import {permissionCheck} from '../../utils/permissionCheck';
import noAssignments from '../../assets/images/noAssignments.png';
import ScoreTableStyle from './ScoreTable';
import {formatElapsedTime} from "../../utils/time";
import ScreenShareIcon from "@material-ui/icons/ScreenShare";
import VideocamIcon from "@material-ui/icons/Videocam";

/**
 * A customized table row component styled using Material-UI's `withStyles` HOC.
 *
 * This component applies a background color to even rows of a table, utilizing
 * the `theme.palette.action.hover` color from the Material-UI theme.
 *
 * The component is based on the Material-UI `TableRow` component and inherits
 * all its properties and behaviors, with additional styling for alternating row
 * colors.
 *
 * Styles:
 * - Applies a hover-like background color to every even row (`:nth-of-type(even)`)
 *      using the theme palette.
 */
const StyledTableRow = withStyles((theme) => ({
    root: {
        "&:nth-of-type(even)": {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow);

/**
 * ScoreTable is a React functional component that renders a table interface for
 * managing and displaying score-related data.
 * It includes features like sorting, scrolling, loading states, and operations
 * for handling assessor notes.
 *
 * @param {Object} props - The properties passed to the ScoreTable component.
 * @param {Array} props.scoreList - An array containing the list of scores to be displayed in the table.
 * @param {number} props.totalScore - The total score value used for summary or display purposes.
 * @param {Function} props.handleScore - A callback function to refresh or update the score data.
 * @param {boolean} props.assignment_loading - A flag to indicate if data for the table is currently loading.
 * @param {string} props.sortBy - The field by which the table is currently sorted.
 * @param {string} props.sort - The current sort direction, either "asc" or "desc".
 * @param {Function} props.sorting - Function to handle sorting logic for table columns.
 * @param {Function} props.handleSortPageChange - A callback to handle pagination or sort-related page updates.
 * @param {number} props.sortPage - The current page in the sorted or paginated view.
 */
const ScoreTable = ({
    scoreList,
    totalScore,
    handleScore,
    assignment_loading,
    sortBy,
    sort,
    sorting,
    handleSortPageChange,
    sortPage,
}) => {
    const useStyles = makeStyles((theme) => ScoreTableStyle(theme));
    const classes = useStyles();
    const [assessor, setAssessor] = useState(false);
    const [responseId, setResponseId] = useState("");
    const [notes, setNotes] = React.useState("");
    const [noteStatus, setNoteStatus] = React.useState("view");
    const [scrollLeft, setScrollLeft] = useState(0);

    useEffect(() => {
        document.getElementById("tableDiv").scrollLeft = scrollLeft;
    }, [scrollLeft]);

    /**
     * Handles the opening of a dialog by setting the necessary state variables based on the provided data.
     * Determines the note status (view, edit, or add) based on user permissions and the presence of existing assessor notes.
     *
     * @param {Object} row - The data object containing information for the dialog.
     * @param {number|string} row.response_id - The unique identifier related to the response.
     * @param {string} row.assessorNotes - The existing notes created by the assessor.
     *
     * @throws {Error} If any permission checks fail or required states are not properly set, this function may result in unintended behavior.
     */
    const openDialog = (row) => {
        setResponseId(row.response_id);
        setNotes(row.assessor_notes);
        setAssessor(true);
        if (
            !permissionCheck(
                DashboardPermissions.assessor_notes.update.task,
                DashboardPermissions.assessor_notes.update.type,
            ) &&
            !permissionCheck(
                DashboardPermissions.assessor_notes.create.task,
                DashboardPermissions.assessor_notes.create.type,
            ) &&
            permissionCheck(
                DashboardPermissions.assessor_notes.view.task,
                DashboardPermissions.assessor_notes.view.type,
            )
        ) {
            setNoteStatus("view");
        } else if (
            permissionCheck(
                DashboardPermissions.assessor_notes.update.task,
                DashboardPermissions.assessor_notes.update.type,
            ) ||
            permissionCheck(
                DashboardPermissions.assessor_notes.create.task,
                DashboardPermissions.assessor_notes.create.type,
            )
        ) {
            setNoteStatus(row.assessor_notes ? "edit" : "add");
        }
    };

    /**
     * Closes the current context or modal by resetting relevant state values.
     *
     * This function performs the following actions:
     * - Disables the assessor state by setting it to false.
     * - Clears the responseId value by setting it to an empty string.
     */
    const handleClose = () => {
        setAssessor(false);
        setResponseId("");
    };

    /**
     * Asynchronous function to handle the addition of assessor notes.
     * This function saves the provided assessor notes using the `SaveAssessorNotes` function,
     * updates the state to indicate the assessor process is complete,
     * and triggers the scoring process.
     *
     * @function
     * @async
     * @returns {Promise<void>} Resolves after saving the assessor notes, updating state, and handling scoring.
     */
    const handleAdd = async () => {
        await SaveAssessorNotes(responseId, {assessor_notes: notes});
        setAssessor(false);
        handleScore();
    };

    /**
     * Asynchronous function to handle the edit action for assessor notes.
     *
     * This function performs the following steps:
     * 1. Saves updated assessor notes using the `SaveAssessorNotes` function.
     * 2. Updates the state to indicate that the assessor interaction is complete.
     * 3. Invokes the scoring process through the `handleScore` function.
     *
     * @function handleEdit
     * @async
     * @returns {Promise<void>} A promise that resolves once the editing and associated processes are complete.
     */
    const handleEdit = async () => {
        await SaveAssessorNotes(responseId, {assessor_notes: notes}, true);
        setAssessor(false);
        handleScore();
    };

    /**
     * Handles the scroll event and updates the scroll position.
     *
     * This function captures the horizontal scroll position of the target element
     * from the native event and updates the state variable `scrollLeft`.
     *
     * @param {Object} event - The scroll event object.
     * @param {Object} event.nativeEvent - The native event object.
     * @param {Object} event.nativeEvent.target - The target element of the scroll event.
     * @param {number} event.nativeEvent.target.scrollLeft - The horizontal scroll position of the target element.
     */
    const scroll = (event) => {
        setScrollLeft(event.nativeEvent.target.scrollLeft);
    };

    /**
     * LoadTable is a function that sets the horizontal scroll position
     * of the HTML element with the ID "tableDiv" to the value of the
     * variable `scrollLeft`.
     *
     * The function does not return any value and relies on the existence
     * of both the "tableDiv" element and the `scrollLeft` variable in
     * the surrounding scope.
     */
    const LoadTable = () => {
        document.getElementById("tableDiv").scrollLeft = scrollLeft;
    };

    return (
        <>
            {assignment_loading && scoreList.length === 0 ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "70vh",
                    }}

                >
                    <CircularProgress color="primary"/>
                </div>
            ) : (
                <Paper>
                    <TableContainer
                        ref={LoadTable}
                        id="tableDiv"
                        className={classes.tableContainer}
                        onScroll={scroll}

                    >
                        <Table
                            aria-label="assignment table"
                            className={classes.tableContainer}

                        >
                            <TableHead className={classes.thead}>
                                <TableRow>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "sim" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "sim" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("sim")}

                                        >
                                            SIM NAME
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "level" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "level" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("level")}

                                        >
                                            LEVEL
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "member" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "member" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("member")}

                                        >
                                            MEMBER
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "keywordAccuracy" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "keywordAccuracy" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("keywordAccuracy")}

                                        >
                                            KEYWORD ACCURACY
                                        </TableSortLabel>
                                    </TableCell>

                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={
                                            sortBy === "correct_selections" ? sort : false
                                        }

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "correct_selections" ?
                                                sort :
                                                "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("correct_selections")}

                                        >
                                            DATA SELECTION
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "correct_entries" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "correct_entries" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("correct_entries")}

                                        >
                                            TYPING ACCURACY
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={
                                            sortBy === "overall_percentage" ? sort : false
                                        }

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "overall_precentage" ?
                                                sort :
                                                "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("overall_percentage")}

                                        >
                                            OVERALL DATA ACCURACY
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "total_time" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "total_time" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("total_time")}

                                        >
                                            TOTAL TIME
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "wrapSeconds" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "wrapSeconds" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("wrapSeconds")}

                                        >
                                            WRAP-UP TIME
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "start_date" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "start_date" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("start_date")}

                                        >
                                            START DATE
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "completed_date" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "completed_date" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("completed_date")}

                                        >
                                            COMPLETED DATE
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={
                                            sortBy === "sent_for_review_date" ? sort : false
                                        }

                                    >
                                        <TableSortLabel
                                            direction={
                                                sortBy === "sent_for_review_date" ? sort : "asc"
                                            }
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("sent_for_review_date")}

                                        >
                                            SEND FOR REVIEW DATE
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "review_status" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "review_status" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("review_status")}

                                        >
                                            REVIEW STATUS
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "reviewed_on" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "reviewed_on" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("reviewed_on")}

                                        >
                                            REVIEWED ON
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        className={classes.heading}
                                        sortDirection={sortBy === "reviewed_by" ? sort : false}

                                    >
                                        <TableSortLabel
                                            direction={sortBy === "reviewed_by" ? sort : "asc"}
                                            active
                                            IconComponent={ArrowDropDownIcon}
                                            onClick={() => sorting("reviewed_by")}

                                        >
                                            REVIEWER NAME
                                        </TableSortLabel>
                                    </TableCell>
                                    {permissionCheck(
                                        DashboardPermissions.assessor_notes.view.task,
                                        DashboardPermissions.assessor_notes.view.type,
                                    ) ? (
                                        <TableCell
                                            className={classes.heading}
                                            sortDirection={sortBy === "assessor_notes" ?
                                                sort :
                                                false}

                                        >
                                            <TableSortLabel
                                                direction={sortBy === "assessor_notes" ?
                                                    sort :
                                                    "asc"}
                                                active
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={() => sorting("assessor_notes")}

                                            >
                                                ASSESSOR NOTES
                                            </TableSortLabel>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scoreList.map((row, index) => (
                                    <StyledTableRow
                                        key={index}
                                    >
                                        <TableCell>
                                            {row.screen_recording && row.screen_recording.length ? (
                                                <Tooltip
                                                    title="Has Screen Recording Enabled"
                                                    placement="top"

                                                >
                                                    <div
                                                        style={{
                                                            marginRight: "1em",
                                                            verticalAlign: "middle",
                                                            display: "inline-block",
                                                        }}

                                                    >
                                                        <ScreenShareIcon
                                                            fontSize="small"

                                                        />
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <></>
                                            )}
                                            {row.screen_recording_urls.length ? (
                                                <Tooltip
                                                    title={`User has ${row.screen_recording_urls.length} screen recordings in one or more levels of this SIM`}
                                                    placement="top"

                                                >
                                                    <div
                                                        style={{
                                                            marginRight: "1em",
                                                            verticalAlign: "middle",
                                                            display: "inline-block",
                                                        }}

                                                    >
                                                        <VideocamIcon fontSize="small"/>
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <></>
                                            )}
                                            {row.sim}
                                        </TableCell>
                                        <TableCell>{row.level}</TableCell>
                                        <TableCell>{row.member}</TableCell>
                                        <TableCell className={classes.txtAlign}>
                                            {row.keywordAccuracy
                                                ? row.keywordAccuracy === "N/A"
                                                    ?
                                                    "N/A"
                                                    :
                                                    `${Math.min(100,
                                                        Math.max(0, row.keywordAccuracy)
                                                    )}%`
                                                : "------"}
                                        </TableCell>
                                        <TableCell className={classes.txtAlign}>
                                            {row.correct_selections
                                                ? row.correct_selections === "N/A"
                                                    ?
                                                    "N/A"
                                                    :
                                                    `${row.correct_selections} of ${row.total_selections}`
                                                : "------"}
                                        </TableCell>
                                        <TableCell className={classes.txtAlign}>
                                            {row.correct_entries
                                                ? row.correct_entries === "N/A"
                                                    ?
                                                    "N/A"
                                                    :
                                                    `${row.correct_entries} of ${row.total_entries}`
                                                : "------"}
                                        </TableCell>
                                        <TableCell>
                                            {row.overall_percentage
                                                ? row.overall_percentage === "N/A"
                                                    ? "N/A"
                                                    : row.overall_percentage
                                                : "------"}
                                        </TableCell>
                                        {/*<TableCell>{row.total_time}</TableCell>*/}
                                        <TableCell>
                                            {formatElapsedTime(row.total_seconds)}
                                        </TableCell>
                                        <TableCell>
                                            {formatElapsedTime(row.wrapSeconds)}
                                        </TableCell>
                                        <TableCell className={classes.date}>
                                            {row.start_date
                                                ? moment(row.start_date)
                                                    .local()
                                                    .format("YYYY-MMM-DD h:mm A")
                                                : moment(row.createdAt)
                                                    .local()
                                                    .format("YYYY-MMM-DD h:mm A")}
                                        </TableCell>
                                        <TableCell className={classes.date}>
                                            {!row.exit_or_restart && row.completed_date
                                                ? moment(row.completed_date)
                                                    .local()
                                                    .format("YYYY-MMM-DD h:mm A")
                                                : "------"}
                                        </TableCell>
                                        <TableCell className={classes.date}>
                                            {row.sent_for_review_date
                                                ? moment(row.sent_for_review_date)
                                                    .local()
                                                    .format("YYYY-MMM-DD h:mm A")
                                                : "------"}
                                        </TableCell>
                                        <TableCell className={classes.txtAlign}>
                                            {row.status === "PASS" || row.status === "NO PASS"
                                                ? row.review_status
                                                    ? "PASS"
                                                    : "NO PASS"
                                                : "------"}
                                        </TableCell>
                                        <TableCell>
                                            {row.status === "PASS" || row.status === "NO PASS"
                                                ? moment(row.reviewed_date)
                                                    .local()
                                                    .format("YYYY-MMM-DD h:mm A")
                                                : "------"}
                                        </TableCell>
                                        <TableCell>
                                            {row.status === "PASS" || row.status === "NO PASS"
                                                ? row.reviewed_by
                                                : "------"}
                                        </TableCell>
                                        {!permissionCheck(
                                            DashboardPermissions.assessor_notes.update.task,
                                            DashboardPermissions.assessor_notes.update.type,
                                        ) &&
                                        !permissionCheck(
                                            DashboardPermissions.assessor_notes.create.task,
                                            DashboardPermissions.assessor_notes.create.type,
                                        ) &&
                                        permissionCheck(
                                            DashboardPermissions.assessor_notes.view.task,
                                            DashboardPermissions.assessor_notes.view.type,
                                        ) ? (
                                            <TableCell>
                                                <div
                                                    onClickCapture={() => openDialog(row)}
                                                    className={classes.assessor}
                                                    style={{
                                                        ...(row.assessor_notes
                                                            ? {
                                                                borderRadius: "5%",
                                                                background: "inherit",
                                                                color: "#636AD1",
                                                            }
                                                            : {}),
                                                    }}

                                                >
                                                    {row.assessor_notes ? "view" : null}
                                                </div>
                                            </TableCell>
                                        ) : permissionCheck(
                                            DashboardPermissions.assessor_notes.update.task,
                                            DashboardPermissions.assessor_notes.update.type,
                                        ) ||
                                        permissionCheck(
                                            DashboardPermissions.assessor_notes.create.task,
                                            DashboardPermissions.assessor_notes.create.type,
                                        ) ? (
                                            <TableCell>
                                                <div
                                                    onClickCapture={() => openDialog(row)}
                                                    className={classes.assessor}
                                                    style={{
                                                        ...(row.assessor_notes
                                                            ? {
                                                                borderRadius: "5%",
                                                                background: "inherit",
                                                                color: "#636AD1",
                                                            }
                                                            : {}),
                                                    }}

                                                >
                                                    {row.assessor_notes ? "view" : "+"}
                                                </div>
                                            </TableCell>
                                        ) : null}
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div>
                        <div className={classes.paginationLabel}>
                            Showing {(sortPage - 1) * 10 + 1} to{" "}
                            {(sortPage - 1) * 10 +
                                (scoreList && scoreList.length ? scoreList.length : 0)}{" "}
                            of {totalScore || 0} entries
                        </div>
                        <Pagination
                            className={classes.pagination}
                            color="primary"
                            count={totalScore ? Math.ceil(totalScore / 10) : 0}
                            shape="rounded"
                            page={sortPage}
                            onChange={handleSortPageChange}

                        />
                    </div>
                </Paper>
            )}
            <Dialog
                onClose={handleClose}
                open={assessor}
                classes={{paper: classes.dialog}}
                fullWidth
                maxWidth="sm"

            >
                <DialogTitle>Assessor Notes</DialogTitle>
                <DialogContent>
                    <div
                        style={{padding: "0.5rem", width: "27vw", paddingLeft: 2}}

                    >
                        <TextField
                            InputProps={{
                                disableUnderline: true,
                                style: {fontSize: 14, paddingLeft: 8},
                            }}
                            fullWidth
                            multiline
                            minRows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={classes.textArea}
                            placeholder="Type your note here"
                            disabled={
                                !permissionCheck(
                                    DashboardPermissions.assessor_notes.update.task,
                                    DashboardPermissions.assessor_notes.update.type,
                                ) &&
                                !permissionCheck(
                                    DashboardPermissions.assessor_notes.create.task,
                                    DashboardPermissions.assessor_notes.create.type,
                                ) &&
                                permissionCheck(
                                    DashboardPermissions.assessor_notes.view.task,
                                    DashboardPermissions.assessor_notes.view.type,
                                )
                            }

                        />

                        <Button
                            size="small"
                            style={{
                                fontWeight: 600,
                                padding: "3px 11px",
                                backgroundColor: "#F8DEE7",
                                float: "right",
                                color: "#A55B73",
                                margin: "8px 0px",
                            }}
                            onClick={handleClose}

                        >
                            Cancel
                        </Button>
                        {noteStatus === "edit" ? (
                            <Tooltip
                                title={
                                    !permissionCheck(
                                        DashboardPermissions.assessor_notes.update.task,
                                        DashboardPermissions.assessor_notes.update.type,
                                    )
                                        ? "You don't have permission to update"
                                        : ""
                                }
                                placement="top"

                            >
                <span className={classes.editSpan}>
                  <Button
                      size="small"
                      className={classes.button}
                      disabled={
                          !permissionCheck(
                              DashboardPermissions.assessor_notes.update.task,
                              DashboardPermissions.assessor_notes.update.type,
                          )
                      }
                      onClick={handleEdit}

                  >
                    Edit
                  </Button>
                </span>
                            </Tooltip>
                        ) : noteStatus === "add" ? (
                            <Tooltip
                                title={
                                    !permissionCheck(
                                        DashboardPermissions.assessor_notes.create.task,
                                        DashboardPermissions.assessor_notes.create.type,
                                    )
                                        ? "You don't have permission to create"
                                        : ""
                                }
                                placement="top"

                            >
                <span className={classes.editSpan}>
                  <Button
                      size="small"
                      className={classes.button}
                      disabled={
                          !permissionCheck(
                              DashboardPermissions.assessor_notes.create.task,
                              DashboardPermissions.assessor_notes.create.type,
                          )
                      }
                      onClick={handleAdd}

                  >
                    Add
                  </Button>
                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>
            {!assignment_loading && scoreList.length === 0 ? (
                <div className={classes.noData}>
                    <img
                        src={noAssignments}
                        height={55}
                        width={54}
                        alt="noAssignments"

                    />

                    <h3 className={classes.noAssign}>
                        There are no Assignments.
                    </h3>
                </div>
            ) : null}
        </>
    );
};

ScoreTable.propTypes = {
    scoreList: PropTypes.array,
    totalScore: PropTypes.number,
    handleScore: PropTypes.func,
    assignment_loading: PropTypes.bool,
    sorting: PropTypes.func,
    sortBy: PropTypes.string,
    sort: PropTypes.string,
    handleSortPageChange: PropTypes.func,
    sortPage: PropTypes.number,
};

export default ScoreTable;
